import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
} from "recharts";

const STATUS_COLORS = {
  OK: "#10b981",
  WARNING: "#f59e0b",
  ALERT: "#ef4444",
  WARMING: "#8b5cf6",
  INIT: "#8b5cf6",
};

const RISK_BRACKET_COLORS = ["#10b981", "#22c55e", "#84cc16", "#eab308", "#f59e0b", "#f97316", "#ef4444"];

/**
 * Fleet Health Donut Chart - Shows distribution of engine statuses
 */
export function FleetHealthChart({ assets }) {
  const assetList = Object.values(assets);

  if (assetList.length === 0) {
    return (
      <div className="fleet-chart-placeholder">
        <span>Waiting for data...</span>
      </div>
    );
  }

  const statusCounts = {
    OK: 0,
    WARNING: 0,
    ALERT: 0,
    WARMING: 0,
  };

  assetList.forEach((asset) => {
    if (asset.status === "OK") statusCounts.OK++;
    else if (asset.status === "WARNING") statusCounts.WARNING++;
    else if (asset.status === "ALERT") statusCounts.ALERT++;
    else statusCounts.WARMING++; // WARMING or INIT
  });

  const data = [
    { name: "Healthy", value: statusCounts.OK, color: STATUS_COLORS.OK },
    { name: "Warning", value: statusCounts.WARNING, color: STATUS_COLORS.WARNING },
    { name: "Critical", value: statusCounts.ALERT, color: STATUS_COLORS.ALERT },
    { name: "Warming", value: statusCounts.WARMING, color: STATUS_COLORS.WARMING },
  ].filter((d) => d.value > 0);

  const total = assetList.length;
  const healthyPercent = total > 0 ? Math.round((statusCounts.OK / total) * 100) : 0;

  return (
    <div className="fleet-health-chart">
      <div className="health-donut-wrapper">
        <ResponsiveContainer width="100%" height={100}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={30}
              outerRadius={45}
              paddingAngle={2}
              dataKey="value"
              stroke="none"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: "#1e293b",
                border: "1px solid #334155",
                borderRadius: "6px",
                fontSize: "0.65rem",
              }}
              formatter={(value, name) => [`${value} engines`, name]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="health-center-label">
          <span className="health-percent">{healthyPercent}%</span>
          <span className="health-text">Healthy</span>
        </div>
      </div>
      <div className="health-legend">
        {data.map((item) => (
          <div key={item.name} className="legend-item">
            <span className="legend-dot" style={{ background: item.color }} />
            <span className="legend-label">{item.name}</span>
            <span className="legend-value">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Risk Distribution Histogram - Shows how many engines in each risk bracket
 */
export function RiskDistributionChart({ assets }) {
  const assetList = Object.values(assets);

  if (assetList.length === 0) {
    return (
      <div className="fleet-chart-placeholder">
        <span>Waiting for data...</span>
      </div>
    );
  }

  // Create risk brackets
  const brackets = [
    { range: "0-20%", min: 0, max: 0.2, count: 0, color: "#10b981" },
    { range: "20-40%", min: 0.2, max: 0.4, count: 0, color: "#84cc16" },
    { range: "40-60%", min: 0.4, max: 0.6, count: 0, color: "#eab308" },
    { range: "60-80%", min: 0.6, max: 0.8, count: 0, color: "#f97316" },
    { range: "80-100%", min: 0.8, max: 1.0, count: 0, color: "#ef4444" },
  ];

  let noRiskCount = 0;

  assetList.forEach((asset) => {
    const risk = asset.risk?.risk_score;
    if (risk === undefined || risk === null) {
      noRiskCount++;
      return;
    }
    for (const bracket of brackets) {
      if (risk >= bracket.min && risk < bracket.max) {
        bracket.count++;
        break;
      }
      if (risk >= 1.0 && bracket.max === 1.0) {
        bracket.count++;
        break;
      }
    }
  });

  // Add "No Data" bracket if there are engines without risk scores
  const data = brackets.map((b) => ({ name: b.range, count: b.count, fill: b.color }));
  if (noRiskCount > 0) {
    data.unshift({ name: "N/A", count: noRiskCount, fill: "#6b7280" });
  }

  return (
    <div className="risk-distribution-chart">
      <ResponsiveContainer width="100%" height={95}>
        <BarChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
          <XAxis
            dataKey="name"
            tick={{ fontSize: 9, fill: "#94a3b8" }}
            axisLine={{ stroke: "#334155" }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 9, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#1e293b",
              border: "1px solid #334155",
              borderRadius: "6px",
              fontSize: "0.65rem",
            }}
            formatter={(value) => [`${value} engines`, "Count"]}
          />
          <Bar dataKey="count" radius={[3, 3, 0, 0]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Fleet Risk Trend - Shows average fleet risk over time
 */
export function FleetRiskTrend({ riskHistory }) {
  // Aggregate risk across all assets by time
  const timePoints = new Map();

  for (const [assetId, history] of Object.entries(riskHistory)) {
    for (const point of history) {
      const key = point.cycle;
      if (!timePoints.has(key)) {
        timePoints.set(key, { cycle: key, risks: [], alerts: 0 });
      }
      const tp = timePoints.get(key);
      tp.risks.push(point.risk_score);
      if (point.alert) tp.alerts++;
    }
  }

  // Convert to array and compute averages
  const data = Array.from(timePoints.values())
    .map((tp) => ({
      cycle: tp.cycle,
      avgRisk: tp.risks.reduce((a, b) => a + b, 0) / tp.risks.length,
      maxRisk: Math.max(...tp.risks),
      alerts: tp.alerts,
    }))
    .sort((a, b) => a.cycle - b.cycle)
    .slice(-30); // Last 30 points

  if (data.length < 2) {
    return (
      <div className="fleet-chart-placeholder">
        <span>Collecting trend data...</span>
      </div>
    );
  }

  return (
    <div className="fleet-risk-trend">
      <ResponsiveContainer width="100%" height={95}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          <XAxis
            dataKey="cycle"
            tick={{ fontSize: 9, fill: "#94a3b8" }}
            axisLine={{ stroke: "#334155" }}
            tickLine={false}
          />
          <YAxis
            domain={[0, 1]}
            tick={{ fontSize: 9, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#1e293b",
              border: "1px solid #334155",
              borderRadius: "6px",
              fontSize: "0.6rem",
            }}
            formatter={(value, name) => [
              `${(value * 100).toFixed(1)}%`,
              name === "avgRisk" ? "Avg Risk" : "Max Risk",
            ]}
            labelFormatter={(label) => `Cycle ${label}`}
          />
          <Line
            type="monotone"
            dataKey="avgRisk"
            stroke="#f59e0b"
            strokeWidth={2}
            dot={false}
            name="avgRisk"
          />
          <Line
            type="monotone"
            dataKey="maxRisk"
            stroke="#ef4444"
            strokeWidth={1}
            strokeDasharray="3 3"
            dot={false}
            name="maxRisk"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
